import { useState } from 'react'

import { X } from 'lucide-react'
import { cn } from '~/shadcn/lib/utils'
import { Button } from '~/shadcn/ui/button/button'
import { Popover, PopoverContent, PopoverTrigger } from '~/shadcn/ui/popover'
import { getVipTooltipConfig } from './vipTooltipConfig'
import { _killPropagation } from '~/@lib/utils/ui.utils'
import { useSettings } from '~/@mid_settings/settings'

type VipBadgeProps = {
  blurTagNames: string[]
  blurTags: string[]
  createdUsingBlurTags?: boolean
  isIconGlowing?: boolean | null
}

export function ProjectVipBadge({
  createdUsingBlurTags,
  blurTagNames,
  blurTags,
  isIconGlowing,
}: VipBadgeProps) {
  const [open, setOpen] = useState(false)
  const { getLandingPageLink } = useSettings()

  if (!blurTagNames.length && !blurTags.length) return null

  const tooltipDetails = getVipTooltipConfig(blurTagNames, getLandingPageLink)
  const IconComponent = tooltipDetails.icon

  function handleClickMoreInfo(e: React.MouseEvent<HTMLElement>) {
    _killPropagation(e)
    window.open(tooltipDetails.href, '_blank')
  }

  function handleBadgeClick(e: React.MouseEvent<HTMLElement>) {
    _killPropagation(e)
    setOpen(true)
  }

  function handlePopoverClose(e?: React.MouseEvent) {
    _killPropagation(e)
    setOpen(false)
  }

  if (!blurTags.length && !createdUsingBlurTags) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          onClick={handleBadgeClick}
          style={{ backgroundColor: tooltipDetails.backgroundColor }}
          className="flex w-[25px]! h-[22px]! cursor-pointer items-center justify-center rounded-[4px]">
          <span
            className={cn(
              'p-1',
              isIconGlowing && 'animate-[glow_1s_ease-in-out_infinite]'
            )}>
            <IconComponent className="text-white" />
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent
        onPointerDownOutside={(e) => {
          e.stopPropagation()
        }}
        onClick={_killPropagation}
        side="bottom"
        align="start"
        className="min-w-[315px] gap-2 rounded-lg border-none bg-[#EBF4FF] p-4 text-black shadow-lg">
        <Button
          variant="ghost"
          size="small"
          onClick={handlePopoverClose}
          className="absolute right-2 top-2 z-10 h-auto p-0">
          <X className="h-5 w-5" />
        </Button>
        <div className="flex flex-col">
          <div className="flex flex-col">
            <p className="text-[13px] font-bold uppercase leading-[21px] text-black">
              {tooltipDetails.title}
            </p>
            <p className="text-[12px] font-semibold leading-6 text-[#262626]">
              {tooltipDetails.description}
            </p>
            {tooltipDetails.buttonTitle && (
              <Button
                onClick={handleClickMoreInfo}
                className="mt-2 h-10 w-[200px] rounded-full bg-[#006EF0] px-[17px] py-[7px] text-[16px] font-semibold leading-[26px] text-[#FFFFFF] hover:bg-[#006EF0]/90">
                {tooltipDetails.buttonTitle}
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
